import React, { useState, useEffect } from 'react';
import { Select, Button } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { sickLeaveService } from '../../services/sickLeaveService';
import { patientService } from '../../services';
import './SickLeave.css';

function SickLeave() {
    const { t } = useTranslation();
    const [certificates, setCertificates] = useState([]);
    const [patients, setPatients] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({
        patientId: '',
        diagnosis: '',
        leaveStartDate: new Date().toISOString().split('T')[0],
        leaveEndDate: '',
        formType: 'government',
        companyName: '',
        restrictions: '',
        doctorNotes: '',
        isFitForLightDuty: false
    });

    useEffect(() => {
        loadCertificates();
        loadPatients();
    }, []);

    const loadPatients = async () => {
        try {
            const response = await patientService.getPatients();
            setPatients(response.data || []);
        } catch (error) {
            console.error('Error loading patients:', error);
        }
    };

    const loadCertificates = async () => {
        try {
            const data = await sickLeaveService.getCertificates();
            setCertificates(data);
        } catch (error) {
            console.error('Error loading certificates:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await sickLeaveService.issueCertificate(form);
            setShowModal(false);
            setForm({
                patientId: '',
                diagnosis: '',
                leaveStartDate: new Date().toISOString().split('T')[0],
                leaveEndDate: '',
                formType: 'government',
                companyName: '',
                restrictions: '',
                doctorNotes: '',
                isFitForLightDuty: false
            });
            loadCertificates();
        } catch (error) {
            alert('Error issuing certificate: ' + error.message);
        }
    };

    const printCertificate = (certificate) => {
        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(`
      <html>
        <head>
          <title>Sick Leave Certificate - ${certificate.certificateNumber}</title>
          <style>
            @page {
                size: A5;
                margin: 0;
            }
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            body { 
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                line-height: 1.6;
                color: #333;
                background: white;
                width: 148mm;
                height: 210mm;
                padding: 0;
                margin: 0 auto;
            }
            .header {
                background: linear-gradient(135deg, #3e2723 0%, #5d4037 100%);
                padding: 15px 20px;
                color: white;
                position: relative;
                overflow: hidden;
            }
            .header::after {
                content: '';
                position: absolute;
                top: 0;
                right: 0;
                width: 120px;
                height: 100%;
                background: white;
                transform: skewX(-15deg);
                transform-origin: top right;
                margin-right: -60px;
                opacity: 0.1;
            }
            .logo {
                width: 40px;
                height: 40px;
                background: #ff9800;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 3px;
                border-radius: 4px;
            }
            .clinic-name {
                font-size: 16px;
                font-weight: 600;
                margin-bottom: 2px;
            }
            .contact-info {
                font-size: 9px;
                opacity: 0.9;
                margin-top: 3px;
            }
            .contact-info div {
                margin: 1px 0;
            }
            .certificate-title {
                text-align: center;
                font-size: 16px;
                font-weight: 700;
                color: #3e2723;
                margin: 12px 0 10px 0;
                text-transform: uppercase;
                letter-spacing: 1.5px;
            }
            .content {
                padding: 0 20px 15px 20px;
            }
            .cert-number {
                text-align: right;
                font-size: 10px;
                color: #666;
                margin-bottom: 10px;
                font-weight: 600;
            }
            .section {
                margin-bottom: 12px;
            }
            .section-title {
                font-size: 10px;
                color: #3e2723;
                font-weight: 700;
                text-transform: uppercase;
                margin-bottom: 5px;
                border-bottom: 2px solid #ff9800;
                padding-bottom: 2px;
            }
            .info-grid {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 8px;
                margin-bottom: 10px;
            }
            .info-item {
                font-size: 10px;
            }
            .info-label {
                color: #666;
                font-size: 9px;
                text-transform: uppercase;
                letter-spacing: 0.3px;
                margin-bottom: 2px;
            }
            .info-value {
                color: #333;
                font-weight: 600;
                font-size: 11px;
            }
            .full-width {
                grid-column: 1 / -1;
            }
            .diagnosis-box {
                background: #f5f5f5;
                padding: 8px;
                border-radius: 4px;
                border-left: 3px solid #ff9800;
                margin: 6px 0;
            }
            .diagnosis-text {
                font-size: 11px;
                color: #333;
                line-height: 1.4;
            }
            .leave-period {
                background: #fff3e0;
                padding: 10px;
                border-radius: 4px;
                margin: 10px 0;
                border: 1px solid #ffe0b2;
            }
            .leave-dates {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 5px;
            }
            .date-box {
                text-align: center;
                flex: 1;
            }
            .date-label {
                font-size: 8px;
                color: #666;
                text-transform: uppercase;
            }
            .date-value {
                font-size: 12px;
                font-weight: 700;
                color: #3e2723;
                margin-top: 2px;
            }
            .arrow {
                font-size: 16px;
                color: #ff9800;
                margin: 0 8px;
            }
            .duration {
                text-align: center;
                font-size: 10px;
                color: #666;
                margin-top: 5px;
                padding-top: 5px;
                border-top: 1px solid #ffe0b2;
            }
            .duration strong {
                color: #ff9800;
                font-size: 14px;
            }
            .notes-section {
                margin-top: 8px;
                font-size: 10px;
                color: #666;
                background: #fafafa;
                padding: 8px;
                border-radius: 4px;
            }
            .signature-area {
                margin-top: 15px;
                padding-top: 10px;
                border-top: 1px solid #e0e0e0;
            }
            .signature-box {
                text-align: right;
            }
            .signature-line {
                border-bottom: 2px solid #333;
                width: 180px;
                margin: 15px 0 5px auto;
            }
            .doctor-name {
                font-weight: 700;
                font-size: 11px;
                color: #3e2723;
            }
            .doctor-title {
                font-size: 9px;
                color: #666;
                margin-top: 2px;
            }
            .footer {
                margin-top: 10px;
                text-align: center;
                font-size: 8px;
                color: #999;
                padding-top: 8px;
                border-top: 1px solid #eee;
            }
            @media print {
                body {
                    width: 148mm;
                    height: 210mm;
                }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Dr</div>
            <div class="clinic-name">${certificate.doctor?.clinicName || certificate.doctor?.name || 'Medical Clinic'}</div>
            <div class="contact-info">
              <div>${certificate.doctor?.specialization || 'Medical Practice Certificate'}</div>
              ${certificate.doctor?.phoneNumber ? `<div>📞 Contact: ${certificate.doctor.phoneNumber}</div>` : ''}
              ${certificate.doctor?.email ? `<div>📧 Email: ${certificate.doctor.email}</div>` : ''}
            </div>
          </div>
          
          <div class="content">
            <div class="certificate-title">Sick Leave Certificate</div>

            <div class="cert-number">
              Certificate No: ${certificate.certificateNumber}<br/>
              Issue Date: ${new Date(certificate.issueDate).toLocaleDateString('en-GB')}
            </div>

            <div class="section">
              <div class="section-title">Patient Information</div>
              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Patient Name</div>
                  <div class="info-value">${certificate.patient?.name || 'N/A'}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">Form Type</div>
                  <div class="info-value" style="text-transform: capitalize;">${certificate.formType}</div>
                </div>
                ${certificate.companyName ? `
                <div class="info-item full-width">
                  <div class="info-label">Company Name</div>
                  <div class="info-value">${certificate.companyName}</div>
                </div>
                ` : ''}
              </div>
            </div>

            <div class="section">
              <div class="section-title">Medical Assessment</div>
              <div class="diagnosis-box">
                <div class="info-label">Diagnosis</div>
                <div class="diagnosis-text">${certificate.diagnosis}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Leave Period</div>
              <div class="leave-period">
                <div class="leave-dates">
                  <div class="date-box">
                    <div class="date-label">From</div>
                    <div class="date-value">${new Date(certificate.leaveStartDate).toLocaleDateString('en-GB')}</div>
                  </div>
                  <div class="arrow">→</div>
                  <div class="date-box">
                    <div class="date-label">To</div>
                    <div class="date-value">${new Date(certificate.leaveEndDate).toLocaleDateString('en-GB')}</div>
                  </div>
                </div>
                <div class="duration">
                  Total Duration: <strong>${certificate.durationDays}</strong> days
                </div>
              </div>
            </div>

            ${certificate.restrictions || certificate.doctorNotes || certificate.isFitForLightDuty ? `
            <div class="section">
              <div class="section-title">Additional Information</div>
              <div class="notes-section">
                ${certificate.restrictions ? `<div style="margin-bottom: 8px;"><strong>Restrictions:</strong> ${certificate.restrictions}</div>` : ''}
                ${certificate.isFitForLightDuty ? `<div style="margin-bottom: 8px;">✓ Patient is fit for light duty work</div>` : ''}
                ${certificate.doctorNotes ? `<div><strong>Doctor's Notes:</strong> ${certificate.doctorNotes}</div>` : ''}
              </div>
            </div>
            ` : ''}

            <div class="signature-area">
              <div class="signature-box">
                <div class="signature-line"></div>
                <div class="doctor-name">${certificate.doctor?.name || 'Doctor Name'}</div>
                <div class="doctor-title">Licensed Medical Practitioner</div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.print();
    };

    return (
        <div className="sickleave-container">
            <div className="page-header">
                <h1>{t('sickLeaveCertificates')}</h1>
                <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    onClick={() => setShowModal(true)}
                    size="large"
                >
                    {t('issueCertificate')}
                </Button>
            </div>

            <div className="certificates-list">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>{t('certificateNumber')}</th>
                            <th>{t('issueDate')}</th>
                            <th>{t('patient')}</th>
                            <th>{t('diagnosis')}</th>
                            <th>{t('leavePeriod')}</th>
                            <th>{t('duration')}</th>
                            <th>{t('type')}</th>
                            <th>{t('actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {certificates.map((cert) => (
                            <tr key={cert._id}>
                                <td>{cert.certificateNumber}</td>
                                <td>{new Date(cert.issueDate).toLocaleDateString()}</td>
                                <td>{cert.patient?.name}</td>
                                <td>{cert.diagnosis}</td>
                                <td>
                                    {new Date(cert.leaveStartDate).toLocaleDateString()} - {new Date(cert.leaveEndDate).toLocaleDateString()}
                                </td>
                                <td>{cert.durationDays} days</td>
                                <td>
                                    <span className={`badge ${cert.formType}`}>{cert.formType}</span>
                                </td>
                                <td>
                                    <button className="btn-sm" onClick={() => printCertificate(cert)}>Print</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('issueSickLeaveCertificate')}</h2>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('patient')} *</label>
                                    <Select
                                        showSearch
                                        value={form.patientId || undefined}
                                        onChange={(value) => setForm({ ...form, patientId: value })}
                                        placeholder={t('searchAndSelectPatient')}
                                        optionFilterProp="label"
                                        style={{ width: '100%' }}
                                        required
                                    >
                                        {patients.map((patient) => (
                                            <Select.Option
                                                key={patient._id}
                                                value={patient._id}
                                                label={`${patient.name} - ${patient.phoneNumber}`}
                                            >
                                                {patient.name} - {patient.phoneNumber}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="form-group">
                                    <label>{t('formType')} *</label>
                                    <select value={form.formType} onChange={(e) => setForm({ ...form, formType: e.target.value })}>
                                        <option value="government">{t('government')}</option>
                                        <option value="company">{t('company')}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('diagnosis')} *</label>
                                <textarea value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} required rows="2" />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('startDate')} *</label>
                                    <input type="date" value={form.leaveStartDate} onChange={(e) => setForm({ ...form, leaveStartDate: e.target.value })} required />
                                </div>
                                <div className="form-group">
                                    <label>{t('endDate')} *</label>
                                    <input type="date" value={form.leaveEndDate} onChange={(e) => setForm({ ...form, leaveEndDate: e.target.value })} required />
                                </div>
                            </div>
                            {form.formType === 'company' && (
                                <div className="form-group">
                                    <label>{t('companyName')}</label>
                                    <input type="text" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} />
                                </div>
                            )}
                            <div className="form-group">
                                <label>{t('restrictions')}</label>
                                <textarea value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} rows="2" placeholder={t('restrictionsPlaceholder')} />
                            </div>
                            <div className="form-group">
                                <label>{t('doctorNotes')}</label>
                                <textarea value={form.doctorNotes} onChange={(e) => setForm({ ...form, doctorNotes: e.target.value })} rows="2" />
                            </div>
                            <div className="form-group">
                                <label>
                                    <input type="checkbox" checked={form.isFitForLightDuty} onChange={(e) => setForm({ ...form, isFitForLightDuty: e.target.checked })} />
                                    {' '}{t('fitForLightDuty')}
                                </label>
                            </div>
                            <div className="modal-actions">
                                <Button onClick={() => setShowModal(false)}>
                                    {t('cancel')}
                                </Button>
                                <Button type="primary" htmlType="submit">
                                    {t('issueCertificate')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SickLeave;
